// Equipment tab — crew & duration, height-band workload, equipment schedule.
import { Clock, HardHat, Layers, RotateCcw, Truck } from "lucide-react";
import TabSkeleton from "./TabSkeleton";
import { useWorkspace } from "../../../hooks/useEstimateWorkspace";
import { Card, CardHeader, Field, IconButton, NumberInput, TD, TD_NUM, TH } from "../../../components/ui";
import { fmtCurrency, fmtDec2, fmtHrs, fmtQty } from "../../../utils/format";

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-[4px] border border-border_soft bg-surface_alt px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">{label}</div>
      <div className="mt-0.5 text-[15px] font-semibold tabular-nums text-text_primary">{value}</div>
      {sub && <div className="text-[11px] text-text_muted">{sub}</div>}
    </div>
  );
}

export default function EquipmentTab() {
  const { calc, draft, isLocked, setProject, setOverride } = useWorkspace();
  if (!calc || !draft) return <TabSkeleton cards={3} />;

  const { crew, bands, items, total, durationDays } = calc.equipment;
  const bandTotals = bands.reduce(
    (a, b) => ({ hrs: a.hrs + b.hrs, daysInt: a.daysInt + b.daysInt, daysExt: a.daysExt + b.daysExt }),
    { hrs: 0, daysInt: 0, daysExt: 0 }
  );

  const crewInput = (key, label) => (
    <Field label={label} htmlFor={`crew-${key}`}>
      <NumberInput
        id={`crew-${key}`}
        min={1}
        max={30}
        step={1}
        value={draft.project[key]}
        disabled={isLocked}
        onChange={(v) => setProject({ [key]: v === "" ? "" : Math.max(1, Math.round(v)) })}
        aria-label={label}
      />
    </Field>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader icon={HardHat} title="Crew & Duration" subtitle="Days = hours ÷ (crew size × 8 hrs/day)" />
        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4 xl:grid-cols-7">
          {crewInput("interiorCrew", "Interior crew size")}
          {crewInput("exteriorCrew", "Exterior crew size")}
          <Stat label="Interior hrs" value={fmtHrs(crew.intHrs)} />
          <Stat label="Exterior hrs" value={fmtHrs(crew.extHrs)} />
          <Stat label="Interior days" value={fmtQty(crew.intDays)} sub={`${crew.intCrew} painters`} />
          <Stat label="Exterior days" value={fmtQty(crew.extDays)} sub={`${crew.extCrew} painters`} />
          <Stat label="Project duration" value={`${durationDays} days`} sub="Int + Ext, rounded up" />
        </div>
      </Card>

      <Card>
        <CardHeader icon={Layers} title="Height-band workload" subtitle="Hours by access band, split by interior / exterior crew" />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" aria-label="Height-band workload">
            <thead>
              <tr>
                <th className={TH}>Band</th>
                <th className={`${TH} text-right`}>Access mult.</th>
                <th className={`${TH} text-right`}>Hrs</th>
                <th className={`${TH} text-right`}>Days (Int)</th>
                <th className={`${TH} text-right`}>Days (Ext)</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((b) => (
                <tr key={b.id}>
                  <td className={`${TD} ${b.hrs > 0 ? "" : "text-text_muted"}`}>{b.label}</td>
                  <td className={TD_NUM}>×{fmtDec2(b.mult)}</td>
                  <td className={TD_NUM}>{fmtHrs(b.hrs)}</td>
                  <td className={TD_NUM}>{fmtQty(b.daysInt)}</td>
                  <td className={TD_NUM}>{fmtQty(b.daysExt)}</td>
                </tr>
              ))}
              <tr className="bg-surface_alt font-semibold">
                <td className={`${TD} font-semibold`}>Total</td>
                <td className={TD_NUM} />
                <td className={`${TD_NUM} font-semibold`}>{fmtHrs(bandTotals.hrs)}</td>
                <td className={`${TD_NUM} font-semibold`}>{fmtQty(bandTotals.daysInt)}</td>
                <td className={`${TD_NUM} font-semibold`}>{fmtQty(bandTotals.daysExt)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader
          icon={Truck}
          title="Equipment schedule"
          subtitle="Quantities are driven by the takeoff; type a value to override"
          actions={<span className="text-[12.5px] font-semibold tabular-nums text-text_primary">{fmtCurrency(total)}</span>}
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" aria-label="Equipment schedule">
            <thead>
              <tr>
                <th className={TH}>Item</th>
                <th className={TH}>Basis</th>
                <th className={`${TH} text-right`}>Rate</th>
                <th className={`${TH} text-right`}>Qty / Days</th>
                <th className={`${TH} text-right`}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td className={TD}>
                    <div className="font-medium">{it.item}</div>
                    <div className="text-[11px] text-text_muted">{it.basisNote}</div>
                  </td>
                  <td className={TD}>{it.basis}</td>
                  <td className={TD_NUM}>{fmtCurrency(it.rate)}</td>
                  <td className={TD_NUM}>
                    <div className="flex items-center justify-end gap-1.5">
                      <NumberInput
                        value={it.overridden ? it.qty : ""}
                        placeholder={String(it.computedQty)}
                        min={0}
                        disabled={isLocked}
                        onChange={(v) => setOverride("equipment", it.id, v === "" ? null : Math.max(0, v))}
                        className={`!w-20 ${it.overridden ? "text-info font-semibold" : ""}`}
                        aria-label={`${it.item} quantity`}
                      />
                      {it.overridden && !isLocked ? (
                        <IconButton icon={RotateCcw} size="xs" variant="ghost" label={`Reset ${it.item} to ${it.computedQty}`} onClick={() => setOverride("equipment", it.id, null)} />
                      ) : (
                        <span className="w-7" aria-hidden="true" />
                      )}
                    </div>
                  </td>
                  <td className={`${TD_NUM} ${it.cost > 0 ? "" : "text-text_muted"}`}>{fmtCurrency(it.cost)}</td>
                </tr>
              ))}
              <tr className="bg-surface_alt">
                <td className={`${TD} font-semibold`} colSpan={4}>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} aria-hidden="true" /> Total equipment
                  </span>
                </td>
                <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
